import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion as Motion } from "framer-motion";

import api from "@/utils/axios";
import Loading from "@/components/Loading/Loading";

import cls from "./LoungeJoinSingle.module.scss";

const slideIn = {
  hidden: { opacity: 0, y: 12 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 220, damping: 22 },
  },
};

const fmtDate = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleString();
};

const statusTone = (s) => {
  const v = String(s || "").toLowerCase();
  if (v === "pending") return "pending";
  if (v === "accepted") return "accepted";
  if (v === "rejected") return "rejected";
  return "neutral";
};

const LoungeJoinSingle = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clubJoinRequestId } = useParams();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [data, setData] = useState(null);

  const [busyAccept, setBusyAccept] = useState(false);
  const [busyReject, setBusyReject] = useState(false);

  const [showRejectForm, setShowRejectForm] = useState(false);
  const [reason, setReason] = useState("");
  const [formErr, setFormErr] = useState("");

  const clubId = data?.club?.clubId || "";
  const memberId = data?.sender?.memberId || "";
  const profilePath =
    data?.sender?.profilePath ||
    (memberId ? `/lounge/member-profile/${memberId}` : null);

  const requestsPath = clubId
    ? `/lounge/clubs/single/${clubId}/membership-requests`
    : "/lounge/clubs-feed";

  const clubPath = clubId
    ? `/lounge/clubs/single/${clubId}`
    : "/lounge/clubs-feed";

  const joinStatus = data?.joinRequest?.status || "";
  const tone = statusTone(joinStatus);

  const canAct = String(joinStatus).toLowerCase() === "pending";

  const fetchIt = async () => {
    try {
      setLoading(true);
      setErr("");
      setFormErr("");
      setShowRejectForm(false);
      setReason("");

      const res = await api.get(
        `/v1/lounge/clubs/join-requests/${clubJoinRequestId}`,
      );
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubJoinRequestId]);

  const clubMetaLine = useMemo(() => {
    const c = data?.club;
    if (!c) return "";
    const parts = [];
    if (c.cityName) parts.push(c.cityName);
    if (c.streetName) parts.push(c.streetName);
    if (c.clubNo !== null && c.clubNo !== undefined && c.clubNo !== "") {
      parts.push(`${t("loungeJoinSingle.clubNo")}: ${c.clubNo}`);
    }
    return parts.join(" • ");
  }, [data, t]);

  const onBack = () => navigate(-1);

  const onAccept = async () => {
    if (!clubJoinRequestId) return;
    try {
      setBusyAccept(true);
      setErr("");
      await api.post(
        `/v1/lounge/clubs/join-requests/${clubJoinRequestId}/accept`,
        {},
      );
      navigate(requestsPath);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || t("common.error"));
    } finally {
      setBusyAccept(false);
    }
  };

  const onReject = async () => {
    setFormErr("");
    if (!reason.trim()) {
      setFormErr(t("loungeJoinSingle.reject.reasonRequired"));
      return;
    }

    try {
      setBusyReject(true);
      setErr("");
      await api.post(
        `/v1/lounge/clubs/join-requests/${clubJoinRequestId}/reject`,
        {
          reason: reason.trim(),
        },
      );
      navigate(requestsPath);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || t("common.error"));
    } finally {
      setBusyReject(false);
    }
  };

  if (loading) {
    return (
      <Loading
        fullscreen
        titleKey="loading.title"
        subtitleKey="loading.subtitle"
        showTips
        cycleMs={1400}
      />
    );
  }

  if (err && !data) {
    return (
      <div className={cls.page}>
        <div className={cls.pageInner}>
          <div className={cls.errBox}>{err}</div>
          <div className={cls.topActions}>
            <button
              type="button"
              className={`${cls.nuBtn} ${cls.nuBtnGhost}`}
              onClick={onBack}
            >
              {t("common.back")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const clubName = data?.club?.clubName || t("loungeJoinSingle.clubFallback");
  const senderName =
    data?.sender?.username || t("loungeJoinSingle.senderFallback");
  const avatarUrl = data?.sender?.avatar?.url || "";

  const jr = data?.joinRequest || {};
  const createdAt = fmtDate(jr.createdAt);
  const decidedAt = fmtDate(jr.decidedAt);
  const message = (jr.message || "").trim();

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        {/* Top actions */}
        <div className={cls.topActions}>
          <button
            type="button"
            className={`${cls.nuBtn} ${cls.nuBtnGhost}`}
            onClick={onBack}
          >
            {t("common.back")}
          </button>

          <Link to={clubPath} className={`${cls.nuBtn} ${cls.nuBtnInfo}`}>
            {t("loungeJoinSingle.actions.visitClub")}
          </Link>

          {profilePath ? (
            <Link to={profilePath} className={`${cls.nuBtn} ${cls.nuBtnSoft}`}>
              {t("loungeJoinSingle.actions.viewProfile")}
            </Link>
          ) : (
            <button
              type="button"
              className={`${cls.nuBtn} ${cls.nuBtnSoft}`}
              disabled
              title={t("loungeJoinSingle.profileMissing")}
            >
              {t("loungeJoinSingle.actions.viewProfile")}
            </button>
          )}
        </div>

        {/* Main card */}
        <Motion.section
          className={cls.card}
          variants={slideIn}
          initial="hidden"
          animate="show"
        >
          <div className={cls.headerRow}>
            <div className={cls.headerLeft}>
              <span className={cls.kicker}>{t("loungeJoinSingle.kicker")}</span>
              <h1 className={cls.title}>{clubName}</h1>
              {clubMetaLine ? (
                <p className={cls.subTitle}>{clubMetaLine}</p>
              ) : null}
            </div>

            <div className={`${cls.statusPill} ${cls[`status_${tone}`] || ""}`}>
              {t(
                `loungeJoinSingle.status.${String(
                  joinStatus || "unknown",
                ).toLowerCase()}`,
                {
                  defaultValue:
                    joinStatus || t("loungeJoinSingle.status.unknown"),
                },
              )}
            </div>
          </div>

          <div className={cls.grid}>
            {/* Sender card */}
            <div className={cls.panel}>
              <div className={cls.panelTitle}>
                {t("loungeJoinSingle.senderTitle")}
              </div>

              <div className={cls.senderRow}>
                <div className={cls.avatarWrap}>
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt=""
                      className={cls.avatar}
                      draggable="false"
                    />
                  ) : (
                    <div className={cls.avatarFallback} />
                  )}
                </div>

                <div className={cls.senderMeta}>
                  <div className={cls.senderName}>{senderName}</div>
                  <div className={cls.senderSmall}>
                    {memberId ? (
                      <span className={cls.miniTag}>
                        {t("loungeJoinSingle.memberId")}: {memberId}
                      </span>
                    ) : (
                      <span className={cls.miniTag}>
                        {t("loungeJoinSingle.memberId")}: -
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {profilePath ? (
                <Link to={profilePath} className={cls.linkMini}>
                  {t("loungeJoinSingle.actions.viewProfile")} →
                </Link>
              ) : null}
            </div>

            {/* Request details */}
            <div className={cls.panel}>
              <div className={cls.panelTitle}>
                {t("loungeJoinSingle.requestTitle")}
              </div>

              <div className={cls.kv}>
                <div className={cls.kvRow}>
                  <div className={cls.k}>{t("loungeJoinSingle.requestId")}</div>
                  <div className={cls.v}>{clubJoinRequestId}</div>
                </div>

                <div className={cls.kvRow}>
                  <div className={cls.k}>{t("loungeJoinSingle.createdAt")}</div>
                  <div className={cls.v}>{createdAt}</div>
                </div>

                {String(joinStatus).toLowerCase() !== "pending" ? (
                  <div className={cls.kvRow}>
                    <div className={cls.k}>
                      {t("loungeJoinSingle.decidedAt")}
                    </div>
                    <div className={cls.v}>{decidedAt}</div>
                  </div>
                ) : null}

                {message ? (
                  <div className={cls.messageBox}>
                    <div className={cls.messageTitle}>
                      {String(joinStatus).toLowerCase() === "rejected"
                        ? t("loungeJoinSingle.reasonTitle")
                        : t("loungeJoinSingle.messageTitle")}
                    </div>
                    <p className={cls.messageText}>{message}</p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Errors */}
          {err ? <div className={cls.errInline}>{err}</div> : null}

          {/* Actions */}
          {canAct ? (
            <div className={cls.actionsBlock}>
              {!showRejectForm ? (
                <div className={cls.actionButtons}>
                  <button
                    type="button"
                    className={`${cls.nuBtn} ${cls.nuBtnAccept}`}
                    onClick={onAccept}
                    disabled={busyAccept || busyReject}
                  >
                    {busyAccept
                      ? t("loungeJoinSingle.accepting")
                      : t("loungeJoinSingle.accept")}
                  </button>

                  <button
                    type="button"
                    className={`${cls.nuBtn} ${cls.nuBtnReject}`}
                    onClick={() => setShowRejectForm(true)}
                    disabled={busyAccept || busyReject}
                  >
                    {t("loungeJoinSingle.reject")}
                  </button>
                </div>
              ) : (
                <div className={cls.rejectForm}>
                  <div className={cls.rejectTitle}>
                    {t("loungeJoinSingle.rejectTitle")}
                  </div>
                  <p className={cls.rejectHint}>
                    {t("loungeJoinSingle.rejectHint")}
                  </p>

                  <label className={cls.label}>
                    <span className={cls.labelTxt}>
                      {t("loungeJoinSingle.rejected.reasonLabel")}
                    </span>
                    <textarea
                      className={cls.textarea}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder={t(
                        "loungeJoinSingle.rejected.reasonPlaceholder",
                      )}
                      rows={4}
                    />
                  </label>

                  {formErr ? (
                    <div className={cls.formErr}>{formErr}</div>
                  ) : null}

                  <div className={cls.actionButtons}>
                    <button
                      type="button"
                      className={`${cls.nuBtn} ${cls.nuBtnGhost}`}
                      onClick={() => {
                        setShowRejectForm(false);
                        setReason("");
                        setFormErr("");
                      }}
                      disabled={busyReject || busyAccept}
                    >
                      {t("common.cancel")}
                    </button>

                    <button
                      type="button"
                      className={`${cls.nuBtn} ${cls.nuBtnReject}`}
                      onClick={onReject}
                      disabled={busyReject || busyAccept}
                    >
                      {busyReject
                        ? t("loungeJoinSingle.rejecting")
                        : t("loungeJoinSingle.rejectConfirm")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className={cls.doneBox}>
              <div className={cls.doneTitle}>
                {t("loungeJoinSingle.doneTitle")}
              </div>
              <p className={cls.doneText}>{t("loungeJoinSingle.doneText")}</p>

              <div className={cls.actionButtons}>
                <Link
                  to={requestsPath}
                  className={`${cls.nuBtn} ${cls.nuBtnInfo}`}
                >
                  {t("loungeJoinSingle.actions.backToRequests")}
                </Link>
                <Link to={clubPath} className={`${cls.nuBtn} ${cls.nuBtnSoft}`}>
                  {t("loungeJoinSingle.actions.visitClub")}
                </Link>
              </div>
            </div>
          )}
        </Motion.section>
      </div>
    </div>
  );
};

export default LoungeJoinSingle;
